import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Head } from '@inertiajs/react';
import { QRCode } from 'react-qr-code';
import Echo from 'laravel-echo';
import PolaroidBoard from '../../components/LiveWall/PolaroidBoard.jsx';
import CinematicSlide from '../../components/LiveWall/CinematicSlide.jsx';
import SettingsPanel from '../../components/LiveWall/SettingsPanel.jsx';
import OnboardingOverlay from '../../components/LiveWall/OnboardingOverlay.jsx';

const MODES = { CINEMATIC: 'cinematic', MOSAIC: 'mosaic' };
const CONTROLS_HIDE_MS = 3000;
const POLL_FALLBACK_MS = 30000;

const DEFAULT_SETTINGS = {
    theme: 'cork',
    cardSize: 'medium',
    rotation: 'subtle',
    showCaptions: true,
    showNames: true,
    showQr: true,
    slideDuration: 10,
};

function loadSettings() {
    try {
        const saved = localStorage.getItem('lw-settings');
        return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
        return DEFAULT_SETTINGS;
    }
}

function normaliseItem(raw) {
    return {
        id: raw.id,
        file_type: raw.file_type,
        url: raw.url,
        preview_url: raw.previewUrl ?? raw.preview_url ?? null,
        blurhash: raw.blurhash ?? null,
        uploader_name: raw.user?.name ?? raw.uploader_name ?? '',
        caption: raw.caption ?? null,
        is_landscape: raw.is_landscape ?? false,
        isNew: raw.isNew ?? false,
    };
}

export default function LiveIndex({ event, initialMedia, reloadUrl, wsChannel, wsAuthUrl }) {
    const [items, setItems] = useState(() => (initialMedia?.data ?? initialMedia ?? []).map(normaliseItem));
    const [mode, setMode] = useState(() => {
        try {
            const saved = localStorage.getItem('lw-mode');
            return saved === MODES.CINEMATIC ? MODES.CINEMATIC : MODES.MOSAIC;
        } catch {
            return MODES.MOSAIC;
        }
    });
    const [showOnboarding, setShowOnboarding] = useState(() => {
        try { return !localStorage.getItem('lw-onboarding-seen'); }
        catch { return false; }
    });
    const [showControls, setShowControls] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [settings, setSettings] = useState(loadSettings);
    const [newItemCount, setNewItemCount] = useState(0);

    const dismissOnboarding = useCallback(() => {
        try { localStorage.setItem('lw-onboarding-seen', '1'); } catch {}
        setShowOnboarding(false);
    }, []);

    const updateSetting = useCallback((key, value) => {
        setSettings(prev => {
            const next = { ...prev, [key]: value };
            localStorage.setItem('lw-settings', JSON.stringify(next));
            return next;
        });
    }, []);

    const hideTimer = useRef(null);
    const pollTimer = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => { isMounted.current = false; };
    }, []);

    const prependItem = useCallback((raw) => {
        const item = normaliseItem({ ...raw, isNew: true });
        setItems(prev => {
            if (prev.some(p => p.id === item.id)) return prev;
            return [item, ...prev];
        });
        setNewItemCount(prev => prev + 1);
    }, []);

    // Simulate a new photo arrival (dev helper)
    const simulatePhoto = useCallback(() => {
        setItems(prev => {
            if (!prev.length) return prev;
            const source = prev[Math.floor(Math.random() * prev.length)];
            const fakeId = `sim-${Date.now()}`;
            const sim = { ...source, id: fakeId, isNew: true };
            return [sim, ...prev];
        });
        setNewItemCount(prev => prev + 1);
    }, []);

    // WebSocket — initialised lazily only on this page
    useEffect(() => {
        if (!wsChannel || !import.meta.env.VITE_REVERB_APP_KEY) return;

        let echo = null;
        try {
            echo = new Echo({
                broadcaster: 'reverb',
                key: import.meta.env.VITE_REVERB_APP_KEY,
                wsHost: import.meta.env.VITE_REVERB_HOST ?? 'localhost',
                wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
                wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
                forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'http') === 'https',
                enabledTransports: ['ws', 'wss'],
                wsPath: '/ws',
                authEndpoint: wsAuthUrl,
            });
            echo.private(wsChannel).listen('.media.added', prependItem);
        } catch {
            // Polling fallback handles updates
        }

        return () => {
            try { echo?.leave(wsChannel); echo?.disconnect(); } catch {}
        };
    }, [wsChannel, prependItem]);

    // Polling fallback
    const poll = useCallback(async () => {
        if (!reloadUrl) return;
        try {
            const response = await axios.get(reloadUrl);
            const incoming = (response.data?.data ?? response.data ?? []).map(normaliseItem);
            setItems(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newOnes = incoming.filter(p => !existingIds.has(p.id)).map(p => ({ ...p, isNew: true }));
                if (!newOnes.length) return prev;
                setNewItemCount(c => c + 1);
                return [...newOnes, ...prev];
            });
        } catch { /* silent */ } finally {
            if (isMounted.current) {
                pollTimer.current = setTimeout(poll, POLL_FALLBACK_MS);
            }
        }
    }, [reloadUrl]);

    useEffect(() => {
        pollTimer.current = setTimeout(poll, POLL_FALLBACK_MS);
        return () => clearTimeout(pollTimer.current);
    }, [poll]);

    // Controls auto-hide
    const showControlsTemporarily = useCallback(() => {
        setShowControls(true);
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setShowControls(false), CONTROLS_HIDE_MS);
    }, []);

    useEffect(() => {
        const onMove = () => showControlsTemporarily();
        document.addEventListener('mousemove', onMove);
        document.addEventListener('touchstart', onMove);
        showControlsTemporarily();
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('touchstart', onMove);
            clearTimeout(hideTimer.current);
        };
    }, [showControlsTemporarily]);

    const showQr = settings.showQr !== false && event.join_url;
    const [showCastModal, setShowCastModal] = useState(false);
    const [copied, setCopied] = useState(false);

    const copyLink = useCallback(() => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, []);

    return (
        <>
            <Head title={`${event.name} · Live Photo Wall`} />

            <div className="fixed inset-0 bg-black overflow-hidden select-none">

                {/* Main display */}
                <div className="absolute inset-0">
                    {mode === MODES.CINEMATIC ? (
                        <CinematicSlide
                            items={items}
                            newItemCount={newItemCount}
                            slideDurationSec={settings.slideDuration}
                            settings={settings}
                        />
                    ) : (
                        <PolaroidBoard items={items} settings={settings} showQr={showQr} />
                    )}
                </div>

                {/* Controls overlay — auto-hides */}
                <div className={`absolute inset-0 z-30 pointer-events-none transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0'}`}>

                    {/* Top bar */}
                    <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 pt-4 pointer-events-auto">
                        <span className="text-white font-bold text-lg drop-shadow-lg">
                            {event.name}
                        </span>
                        <div className="flex items-center gap-2">
                            {/* Simulate button — dev only */}
                            {import.meta.env.DEV && (
                                <button
                                    onClick={simulatePhoto}
                                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full transition"
                                    title="Simulate a new photo arriving"
                                >
                                    📸 Test
                                </button>
                            )}
                            {/* Mode toggle */}
                            <button
                                onClick={() => setMode(m => {
                                    const next = m === MODES.CINEMATIC ? MODES.MOSAIC : MODES.CINEMATIC;
                                    try { localStorage.setItem('lw-mode', next); } catch {}
                                    return next;
                                })}
                                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full transition"
                            >
                                {mode === MODES.CINEMATIC ? '📌 Board' : '⛶ Cinematic'}
                            </button>
                            {/* Settings */}
                            <button
                                onClick={() => setShowSettings(s => !s)}
                                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm w-8 h-8 rounded-full transition flex items-center justify-center"
                                title="Customize"
                            >
                                ⚙
                            </button>
                        </div>
                    </div>

                    {/* Bottom-right: Cast to TV */}
                    <div className="absolute bottom-5 right-5 pointer-events-auto">
                        <button
                            onClick={() => setShowCastModal(true)}
                            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full px-3 py-2 flex items-center gap-2 text-white/70 hover:text-white text-xs transition"
                        >
                            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M1 18v3h3a3 3 0 0 0-3-3zm0-4v2a5 5 0 0 1 5 5H8a7 7 0 0 0-7-7zm0-4v2a9 9 0 0 1 9 9h2A11 11 0 0 0 1 10zm20-6H3C1.9 4 1 4.9 1 6v2h2V6h18v12h-7v2h7c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
                            </svg>
                            <span className="hidden md:inline">Cast to TV</span>
                        </button>
                    </div>

                </div>

                {/* QR code — always visible */}
                {showQr && (
                    <div className="absolute bottom-5 left-5 z-30 pointer-events-auto">
                        <div className="bg-white rounded-2xl p-3 flex flex-col items-center gap-2">
                            <div className="bg-white p-2 rounded-lg">
                                <QRCode value={event.join_url} size={96} />
                            </div>
                            <span className="text-gray-700 text-xs font-medium text-center leading-tight">
                                Scan to add<br />your photo!
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {showSettings && (
                <SettingsPanel
                    settings={settings}
                    onUpdate={updateSetting}
                    onClose={() => setShowSettings(false)}
                    mode={mode}
                />
            )}

            {showCastModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setShowCastModal(false)}>
                    <div
                        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-purple-600 px-6 py-5 text-white relative">
                            <button
                                onClick={() => setShowCastModal(false)}
                                className="absolute top-4 right-4 text-white/50 hover:text-white transition"
                            >✕</button>
                            <div className="flex items-center gap-2 mb-1">
                                <svg className="w-4 h-4 text-white/70" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M1 18v3h3a3 3 0 0 0-3-3zm0-4v2a5 5 0 0 1 5 5H8a7 7 0 0 0-7-7zm0-4v2a9 9 0 0 1 9 9h2A11 11 0 0 0 1 10zm20-6H3C1.9 4 1 4.9 1 6v2h2V6h18v12h-7v2h7c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
                                </svg>
                                <span className="text-xs font-semibold uppercase tracking-widest text-white/70">Live Photo Wall</span>
                            </div>
                            <h2 className="text-xl font-bold">Cast to your TV</h2>
                            <p className="text-sm text-white/80 mt-1">Show the wall on any screen in seconds.</p>
                        </div>

                        {/* Steps */}
                        <ol className="px-6 py-5 space-y-4">
                            {[
                                'Open this page in Google Chrome on the computer connected to your TV or Chromecast.',
                                'Click the menu in the top-right corner and choose "Cast".',
                                'Select your TV or Chromecast from the list.',
                                'Every guest\'s photo will appear live on your screen.',
                            ].map((text, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                                    <span className="text-gray-600 text-sm leading-snug">{text}</span>
                                </li>
                            ))}
                        </ol>

                        <div className="px-6 pb-5">
                            <button
                                onClick={copyLink}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm py-2.5 rounded-xl transition"
                            >
                                {copied ? '✓ Link copied!' : 'Copy page link'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showOnboarding && <OnboardingOverlay onDismiss={dismissOnboarding} />}
        </>
    );
}
